import { cn } from '@/lib/utils';

/**
 * Sidebar item styles from the HR Module Prototype's Component Map (App shell › Sidebar):
 * - expanded: text-first items; hover changes the text colour only; the active page is primary, bold
 *   and underlined in primary (icon too); sub-items carry no icon and no guide line;
 * - collapsed rail (5rem): the label sits under the icon (11px), the active icon in a tile tinted with
 *   the primary colour at 14%, a neutral tile on hover; no tooltips (labels are visible).
 * Each item is a `group/nav` so its icon and label react to the item's hover.
 *
 * Every part (text, icon, its tile, the underline) changes colour with the same transition, so selecting
 * a page fades it to primary in one smooth step. The underline is always drawn, in a transparent colour
 * until the item is active, because an underline can't fade in or out.
 */
const COLOUR_FADE = 'transition-[color,background-color,text-decoration-color] duration-normal ease-standard';

const UNDERLINE = 'underline decoration-[1.5px] underline-offset-4';

export const navItemClass = ({ active, rail }: { active: boolean; rail: boolean }) =>
  cn(
    'group/nav flex w-full rounded-md text-sm font-medium outline-none',
    COLOUR_FADE,
    'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
    rail
      ? 'flex-col items-center justify-center gap-1 px-0.5 pb-1.5 pt-1 text-muted-foreground'
      : // Group headers are <button>s, which centre text by default.
        'min-h-8 items-center gap-2 px-2 py-1.5 text-left text-sidebar-foreground hover:text-foreground',
    !rail && active && 'font-semibold text-primary hover:text-primary'
  );

export const navIconClass = ({ active, rail }: { active: boolean; rail: boolean }) =>
  cn(
    'shrink-0',
    COLOUR_FADE,
    rail ? 'box-content size-[18px] rounded-md px-2.5 py-1.5' : 'size-[18px]',
    rail &&
      (active
        ? 'bg-[color-mix(in_srgb,oklch(var(--primary))_14%,transparent)] text-primary'
        : 'text-muted-foreground group-hover/nav:bg-sidebar-accent group-hover/nav:text-foreground'),
    !rail && (active ? 'text-primary' : 'text-muted-foreground')
  );

export const navLabelClass = ({ active, rail }: { active: boolean; rail: boolean }) =>
  cn(
    COLOUR_FADE,
    rail
      ? 'line-clamp-2 w-full text-center text-[11px] font-medium leading-[13px]'
      : cn('min-w-0 flex-1 truncate text-left', UNDERLINE, active ? 'decoration-current' : 'decoration-transparent'),
    rail && (active ? 'font-semibold text-primary' : 'group-hover/nav:text-foreground')
  );

/** A page inside an open group: text only, indented to the group's label. */
export const navSubItemClass = (active: boolean) =>
  cn(
    'flex min-h-7 w-full items-center rounded-md py-1 pl-8 pr-2 text-sm outline-none',
    COLOUR_FADE,
    'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
    // Spelled out: Tailwind only generates classes it finds written in full.
    '[&>span]:underline [&>span]:decoration-[1.5px] [&>span]:underline-offset-4',
    '[&>span]:transition-[text-decoration-color] [&>span]:duration-normal [&>span]:ease-standard',
    active
      ? 'font-semibold text-primary [&>span]:decoration-current'
      : 'font-normal text-muted-foreground hover:text-foreground [&>span]:decoration-transparent'
  );
